// Material 3 User Avatar Component & Initials / Color Helpers
(function () {
  const { Avatar: MuiAvatar } = MaterialUI;

  /**
   * Helper to compute avatar initials according to requirements:
   * - If no picture is set, display first character of user display name.
   * - If user has 2 names or more, take only the first letter from 2 names and display it on avatar.
   * - Fallback to username if display name is absent.
   */
  function getAvatarInitials(name) {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  /**
   * Avatar colors without a photo: always the theme accent (primary), so the avatar follows the Material You scheme.
   */
  function getAvatarTones() {
    return { bg: 'var(--m3-primary)', fg: 'var(--m3-on-primary)' };
  }

  /**
   * Material 3 UserAvatar Component
   * Supports image URL or fallback to calculated initials + custom background color
   */
  function UserAvatar({
    user,
    src,
    alt,
    size = 40,
    displayNameOverride,
    sx = {},
    onClick,
    ...props
  }) {
    const rawSrc = src !== undefined ? src : (user ? user.avatarUrl : '');
    // A photo that fails to load (deleted file, offline) falls back to the initials avatar
    const [failedSrc, setFailedSrc] = React.useState(null);
    const hasPicture = Boolean(rawSrc && typeof rawSrc === 'string' && rawSrc.trim() !== '') && failedSrc !== rawSrc;

    const nameToUse = (displayNameOverride !== undefined && displayNameOverride !== null && displayNameOverride.trim() !== '')
      ? displayNameOverride
      : (user ? (user.displayName || user.username || '') : '');

    const initials = getAvatarInitials(nameToUse);
    const { bg, fg } = getAvatarTones();

    // Dynamic responsive font sizing based on 1 vs 2 characters and avatar dimensions
    const numericSize = typeof size === 'number'
      ? size
      : (typeof size === 'object' && size.md ? size.md : 40);

    const fontSize = initials.length > 1
      ? `${Math.max(11, Math.round(numericSize * 0.38))}px`
      : `${Math.max(13, Math.round(numericSize * 0.46))}px`;

    // A clickable avatar is a button: focusable, Enter/Space activate it, named after the person unless the caller labels it
    const button = onClick ? {
      role: 'button',
      tabIndex: 0,
      'aria-label': alt || nameToUse,
      onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } }
    } : {};

    if (hasPicture) {
      return (
        <MuiAvatar
          src={rawSrc}
          alt={alt || nameToUse}
          imgProps={{ onError: () => setFailedSrc(rawSrc) }}
          onClick={onClick}
          sx={{
            width: size,
            height: size,
            bgcolor: 'var(--m3-surface-container-highest)',
            ...sx
          }}
          {...button}
          {...props}
        />
      );
    }

    return (
      <MuiAvatar
        alt={alt || nameToUse}
        onClick={onClick}
        sx={{
          width: size,
          height: size,
          bgcolor: bg,
          color: fg,
          fontFamily: "var(--wb-font-avatar, 'Google Sans', 'Google Sans Text', Roboto, sans-serif)", // Admin › Site settings: avatar initials
          fontSynthesis: 'none',
          fontWeight: 500,
          fontSize: typeof size === 'object' ? (initials.length > 1 ? '1.4rem' : '1.7rem') : fontSize,
          letterSpacing: '0.1px',
          userSelect: 'none',
          ...sx
        }}
        {...button}
        {...props}
      >
        {initials}
      </MuiAvatar>
    );
  }

  window.Components = window.Components || {};
  window.Components.UserAvatar = UserAvatar;
  window.Components.getAvatarInitials = getAvatarInitials;
})();
